import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { BUILDER_RULES, BUILDER_TAGS } from '@/courses/values/courses.values';
import EdInputModel from '@/models/ed-input.model';

@Component({
  name: 'ed-subject-builder-rules',
  components: {
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdSubjectBuilderRulesComponent extends Vue {

  @Prop({ default: () => [] }) readonly lessons: any[];
  @Prop({ default: () => [] }) readonly teachingDays: any[];
  @Prop() readonly ruleOptions: any;

  public ruleSelected = EdInputModel('ruleSelected', BUILDER_RULES.START_LESSON );
  public ruleTypes: any = BUILDER_RULES;
  public fieldOneSelected = EdInputModel('fieldOneSelected', '' );
  public fieldTwoSelected = EdInputModel('fieldTwoSelected', '' );
  public fieldOneList: any = [];
  public fieldTwoList: any = [];
  public fieldOneLabel: any = '';
  public fieldTwoLabel: any = '';
  public fieldOneInfo: any = '';
  public fieldTwoInfo: any = '';
  public isPreviousLesson: any = EdInputModel('isPreviousLesson', false, {}, true);
  public showPreviousCheck: boolean = false;

  mounted() {
    this.ruleSelected = EdInputModel('ruleSelected', BUILDER_RULES.START_LESSON );
    this.fieldOneSelected = EdInputModel('fieldOneSelected', '' );
    this.fieldTwoSelected = EdInputModel('fieldTwoSelected', '' );
    this.setUpdateRule();
  }

  get editionMode() {
    return !!this.ruleOptions?.type;
  }

  get rules() { 
    return [{
      text: this.$i18n.t('courses.subjectBuilder.rulesContainer.startLessonOption'),
      value: BUILDER_RULES.START_LESSON
    },{
      text: this.$i18n.t('courses.subjectBuilder.rulesContainer.tagDayOption'),
      value: BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY
    }];
  }

  get labelPreviousLesson() {
    const value = this.fieldOneSelected?.value ?? this.fieldOneSelected;
    if (!value) {
      return '';
    }

    return this.$t('courses.subjectBuilder.rulesContainer.previousLesson', {
      tag: value.charAt(0) + value.slice(1).toLowerCase(),
    });
  }

  goToRulesList() {
    this.$emit('toggleAddRules')
  }

  emitRule() {
    let rule = this.setRuleToEmit();
    this.$emit('onSubmitRule', rule);
  }

  updateRule() {
    let rule = this.setRuleToEmit();
    this.$emit('onUpdateRule', rule);
  }

  setRuleToEmit() {
    let rule
    if(this.ruleSelected.value === BUILDER_RULES.START_LESSON) {
      rule = {
        startLesson: this.fieldOneSelected?.value ?? this.fieldOneSelected,
        finalLesson: typeof this.fieldTwoSelected === 'object' ? this.fieldTwoSelected?.value : this.fieldTwoSelected,
        type: this.ruleSelected.value
      }
    } else if(this.ruleSelected.value === BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY) {
      rule = {
        label: this.fieldOneSelected?.value ?? this.fieldOneSelected,
        workingDay: typeof this.fieldTwoSelected === 'object' ? this.fieldTwoSelected?.value : this.fieldTwoSelected,
        type: this.ruleSelected.value,
        forcePreviousLesson: this.isPreviousLesson.value
      }
    }
    return rule
  }

  setUpdateRule() {
    if(this.ruleOptions) {
      switch(this.ruleOptions.type) {
        case BUILDER_RULES.START_LESSON:
          this.fieldOneSelected = EdInputModel('fieldOneSelected', this.ruleOptions.startLesson);
          this.fieldTwoSelected = EdInputModel('fieldTwoSelected', this.ruleOptions.finalLesson);
          this.ruleSelected = EdInputModel('ruleSelected', this.ruleOptions.type );
          break;
        case BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY:
          this.fieldOneSelected = EdInputModel('fieldOneSelected', this.ruleOptions.label);
          this.fieldTwoSelected = EdInputModel('fieldTwoSelected', this.ruleOptions.workingDay);
          this.ruleSelected = EdInputModel('ruleSelected', this.ruleOptions.type );
          this.isPreviousLesson.value = this.ruleOptions.forcePreviousLesson;
          break;
        default:
          break;
      }
    }

    this.initLabelAutocomplete();
  }

  isValidField(value) {
    return value !== null && value !== undefined && value.toString().trim() !== '';
  }

  @Watch('ruleSelected', {deep: true} )
  setRuleFields() {
    const hasDataStarOnAndEndOnField =
      this.isValidField(this.fieldOneSelected.value) && this.isValidField(this.fieldTwoSelected.value);

    const onlyHasDataStarOnField =
      this.isValidField(this.fieldOneSelected.value) && !this.isValidField(this.fieldTwoSelected.value);

    if (hasDataStarOnAndEndOnField && this.ruleSelected.value == BUILDER_RULES.START_LESSON) {
      return;
    }

    if (onlyHasDataStarOnField && this.ruleSelected.value == BUILDER_RULES.START_LESSON) {
      return;
    }

    if (!this.ruleSelected || typeof this.ruleSelected !== 'object') {
      return;
    }

    if(!this.editionMode){
      this.fieldOneSelected = EdInputModel('fieldOneSelected', '' );
      this.fieldTwoSelected = EdInputModel('fieldTwoSelected', '' );
    }

    this.initLabelAutocomplete();
  }

  initLabelAutocomplete() {
    switch(this.ruleSelected.value) {
      case BUILDER_RULES.START_LESSON:
        this.showPreviousCheck = false;
        this.fieldOneList = this.lessons.map(el => { return {
          text: `${this.$t('courses.subjectBuilder.lesson')} ${el.fixedSequence}`,
          value: el.fixedSequence
        }});
        this.fieldTwoList = this.fieldOneList.filter(el => el.value >= this.fieldOneSelected.value);
        this.fieldOneLabel = this.$t('courses.subjectBuilder.rulesContainer.startOn');
        this.fieldTwoLabel = this.$t('courses.subjectBuilder.rulesContainer.endOn');
        this.fieldOneInfo = this.$t('courses.subjectBuilder.rulesContainer.startLessonInfo');
        this.fieldTwoInfo = this.$t('courses.subjectBuilder.rulesContainer.endLessonInfo');
        break;
      case BUILDER_RULES.PLACE_LABELS_ON_WORKING_DAY:
        this.showPreviousCheck = true;
        this.fieldOneList = Object.keys(BUILDER_TAGS)
        this.fieldTwoList = this.teachingDays.map(el =>  {
          return {
            text: this.$t(`common.weekdays.${el.toLowerCase()}`),
            value: el
          }
        });
        this.fieldOneLabel = this.$t('courses.subjectBuilder.rulesContainer.tagInfo');
        this.fieldTwoLabel = this.$t('courses.subjectBuilder.rulesContainer.dayInfo');
        break;
      default:
        break;
    }
  }
}